import type { PaginationResult, Trade } from '@monorepo/types';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import { fetchPositionTrades } from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { useQuery } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.ACCOUNT;

interface UsePositionTradesProps {
  tsAccountId: string | undefined;
  positionId: string | undefined;
  enabled?: boolean;
}

/**
 * Hook to fetch trades for a specific position.
 *
 * @throws Error if tsAccountId is missing when the query is enabled
 * @returns Query result with trades data, loading state, and error state
 */
export const usePositionTrades = ({
  tsAccountId,
  positionId,
  enabled = true,
}: UsePositionTradesProps) => {
  const isValidTsAccountId = Boolean(tsAccountId && tsAccountId.length > 0);
  const isValidPositionId = Boolean(positionId && positionId.length > 0);
  const shouldFetch = enabled && isValidTsAccountId && isValidPositionId;

  return useQuery<PaginationResult<Trade>, Error>({
    queryKey: QUERY_KEYS[CONFIG_KEY].POSITION_TRADES(
      tsAccountId ?? '',
      positionId ?? '',
    ),
    queryFn: () => {
      if (!tsAccountId) {
        throw new Error(
          'usePositionTrades: tsAccountId is required. Mongo _id is not accepted.',
        );
      }
      if (!positionId) {
        throw new Error('usePositionTrades: positionId is required.');
      }
      return fetchPositionTrades(tsAccountId, positionId);
    },
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    enabled: shouldFetch,
  });
};
